// Central app store — a React-context port of the Python ViewModel.
// Owns navigation, the studying session, playback (with repeat + auto-advance
// driven by speech-synthesis callbacks), sentence import, and persisted
// settings.
//
// This file is carried over from the React Native port essentially unchanged:
// it never touched a native API, only ./tts and ./storage, both of which have
// browser implementations behind the same interface.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CONTENT_ID,
  Lesson,
  PlayItem,
  Progress,
  RepeatKey,
  Screen,
  SpeedKey,
  emptyItem,
} from './types';
import {
  DEFAULT_PROGRESS,
  buildExport,
  downloadExport,
  loadLessons,
  loadProgress,
  loadUiLang,
  parseImport,
  saveLessons,
  saveProgress,
  saveUiLang,
} from './storage';
import {
  DEFAULT_VOICE,
  SPEED_RATES,
  VoiceOption,
  isSupported as ttsSupported,
  loadEnglishVoices,
  speak,
  stop as ttsStop,
  voiceIdentifierPool,
} from './tts';
import {
  DEFAULT_LANG,
  Lang,
  LangPref,
  detectSystemLang,
  translate,
} from './i18n';
import { Pair, normalize, splitSpeaker } from './utils';

const REPEAT_MAX: Record<RepeatKey, number> = {
  one: 1,
  twice: 2,
  three: 3,
  five: 5,
  infinite: 1e9,
};

interface AddResult {
  ok: number;
  lesson: Lesson;
}

interface StoreValue {
  ready: boolean;
  screen: Screen;
  lessons: Lesson[];
  currentLesson: Lesson | null;
  currentIndex: number;
  currentItem: PlayItem | null;
  cardShowingFront: boolean;
  isSpeaking: boolean;
  progress: Progress;
  voiceOptions: VoiceOption[];
  speechSupported: boolean;

  // dictation
  dictationInput: string;
  dictationChecked: boolean;
  setDictationInput: (v: string) => void;

  // i18n
  lang: Lang;
  langPref: LangPref;
  t: (key: string, vars?: Record<string, string | number>) => string;
  setLang: (pref: LangPref) => void;

  // navigation
  openUnit: (lesson: Lesson) => void;
  startCardPlay: () => void;
  startDictation: () => void;
  startExpressionList: () => void;
  startAddSentences: () => void;
  backToUnitList: () => void;
  backToScenarioMenu: () => void;

  // data
  nextSid: () => number;
  addSentences: (
    target: Lesson | null,
    part: string,
    unit: string,
    pairs: Pair[],
    voiceKey: string,
    persona: boolean,
  ) => Promise<AddResult>;
  deleteUnit: (lesson: Lesson) => void;
  exportData: () => void;
  importData: (text: string) => { lessons: number } | null;

  // helpers
  unitProgress: (lesson: Lesson) => number;
  itemHasAudio: (item: PlayItem) => boolean;
  unitHasAnyAudio: (lesson: Lesson) => boolean;
  isItemSkipped: (index: number) => boolean;
  toggleItemSkipped: (index: number) => void;

  // playback
  playCurrent: () => void;
  replay: () => void;
  togglePlay: () => void;
  stopPlayback: () => void;
  playItemAudio: (index: number) => void;
  goToIndex: (index: number, autoPlay: boolean) => void;
  next: (autoPlay?: boolean) => void;
  prev: (autoPlay?: boolean) => void;
  flipCard: () => void;

  // settings
  setSpeed: (s: SpeedKey) => void;
  setRepeatCount: (r: RepeatKey) => void;
  toggleMeaning: () => void;
  toggleWord: () => void;
  setAutoAdvance: (v: boolean) => void;

  // dictation
  checkDictation: () => boolean;
}

const Ctx = createContext<StoreValue | null>(null);

export function useStore(): StoreValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStore must be used within <StoreProvider>');
  return v;
}

function speakableText(item: PlayItem): string {
  return item.speak_text && item.speak_text.length ? item.speak_text : item.eng_text;
}

/** Map each speaker name to a distinct voice id, cycling the pool (persona). */
function assignPersonaVoices(
  speakers: Array<string | null>,
  fallback: string | undefined,
  pool: string[],
): Array<string | undefined> {
  if (!pool.length) return speakers.map(() => fallback);
  const assigned = new Map<string, string>();
  return speakers.map((sp) => {
    if (!sp) return fallback;
    const key = sp.toUpperCase();
    if (!assigned.has(key)) assigned.set(key, pool[assigned.size % pool.length]);
    return assigned.get(key);
  });
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [screen, setScreen] = useState<Screen>('unit_list');
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [currentSid, setCurrentSid] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardShowingFront, setCardShowingFront] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [skipped, setSkipped] = useState<Record<number, boolean>>({});
  const [progress, setProgress] = useState<Progress>({ ...DEFAULT_PROGRESS });
  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);
  const [dictationInput, setDictationInput] = useState('');
  const [dictationChecked, setDictationChecked] = useState(false);
  const [langPref, setLangPref] = useState<LangPref>('system');
  const [lang, setLangState] = useState<Lang>(DEFAULT_LANG);
  const speechSupported = useMemo(() => ttsSupported(), []);

  const currentLesson = useMemo(
    () => lessons.find((l) => l.sid === currentSid) ?? null,
    [lessons, currentSid],
  );
  const currentItem = useMemo(() => {
    if (currentLesson && currentIndex >= 0 && currentIndex < currentLesson.items.length) {
      return currentLesson.items[currentIndex];
    }
    return null;
  }, [currentLesson, currentIndex]);

  // ---- refs kept in sync so speech callbacks read fresh values -------------
  const settingsRef = useRef({ progress, screen });
  const lessonRef = useRef<Lesson | null>(currentLesson);
  const lessonsRef = useRef<Lesson[]>(lessons);
  const indexRef = useRef(currentIndex);
  const skippedRef = useRef(skipped);
  const playCountRef = useRef(0);
  const playGenRef = useRef(0);
  const autoAdvanceRef = useRef<() => void>(() => {});
  settingsRef.current = { progress, screen };
  lessonRef.current = currentLesson;
  lessonsRef.current = lessons;
  indexRef.current = currentIndex;
  skippedRef.current = skipped;

  // ---- initial load --------------------------------------------------------
  useEffect(() => {
    (async () => {
      const [ls, pr, pref, voices] = await Promise.all([
        loadLessons(),
        loadProgress(),
        loadUiLang(),
        loadEnglishVoices(),
      ]);
      setLessons(ls);
      setProgress(pr);
      setLangPref(pref);
      const resolved = pref === 'system' ? detectSystemLang() : pref;
      setLangState(resolved);
      const opts: VoiceOption[] = [
        { key: DEFAULT_VOICE, label: translate(resolved, 'voice.default') },
        ...voices.map((v) => ({
          key: v.identifier,
          label: v.name || v.identifier,
          identifier: v.identifier,
        })),
      ];
      setVoiceOptions(opts);
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(lang, key, vars),
    [lang],
  );

  // rebuild the default-voice label when language changes
  useEffect(() => {
    setVoiceOptions((prev) =>
      prev.map((o) => (o.key === DEFAULT_VOICE ? { ...o, label: translate(lang, 'voice.default') } : o)),
    );
  }, [lang]);

  // Keep <html lang> in step with the UI language — screen readers pick
  // pronunciation from it, and browsers use it to offer page translation.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // While following the browser, react to the user changing their language
  // preference mid-session instead of waiting for a reload.
  useEffect(() => {
    if (langPref !== 'system') return;
    const onChange = () => setLangState(detectSystemLang());
    window.addEventListener('languagechange', onChange);
    return () => window.removeEventListener('languagechange', onChange);
  }, [langPref]);

  // ---- persistence helpers -------------------------------------------------
  const persistProgress = useCallback((next: Progress) => {
    setProgress(next);
    void saveProgress(next);
  }, []);

  const savePosition = useCallback(
    (index: number) => {
      const sid = lessonRef.current?.sid;
      if (sid == null) return;
      const next: Progress = {
        ...settingsRef.current.progress,
        last_positions: { ...settingsRef.current.progress.last_positions, [sid]: index },
      };
      persistProgress(next);
    },
    [persistProgress],
  );

  // ---- playback ------------------------------------------------------------
  const doSpeak = useCallback((item: PlayItem) => {
    const gen = ++playGenRef.current;
    const rate = SPEED_RATES[settingsRef.current.progress.repeat_speed];
    setIsSpeaking(true);
    speak(speakableText(item), {
      rate,
      voiceId: item.voice,
      onDone: () => {
        if (gen !== playGenRef.current) return;
        handleCompletion(item);
      },
      onStopped: () => {
        if (gen === playGenRef.current) setIsSpeaking(false);
      },
      onError: () => {
        if (gen === playGenRef.current) setIsSpeaking(false);
      },
    });
    // handleCompletion is defined below; hoisted via function declaration
    function handleCompletion(spoken: PlayItem) {
      const s = settingsRef.current;
      const max = REPEAT_MAX[s.progress.repeat_count] ?? 1;
      playCountRef.current += 1;
      if (playCountRef.current < max) {
        doSpeak(spoken);
      } else {
        playCountRef.current = 0;
        if (s.progress.auto_advance && s.screen === 'card_play') {
          autoAdvanceRef.current();
        } else {
          setIsSpeaking(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPlayback = useCallback(() => {
    playGenRef.current++; // invalidate pending callbacks
    playCountRef.current = 0;
    ttsStop();
    setIsSpeaking(false);
  }, []);

  const goToIndex = useCallback(
    (index: number, autoPlay: boolean) => {
      const lesson = lessonRef.current;
      if (!lesson || index < 0 || index >= lesson.items.length) return;
      stopPlayback();
      setCurrentIndex(index);
      indexRef.current = index;
      setCardShowingFront(true);
      setDictationInput('');
      setDictationChecked(false);
      savePosition(index);
      if (autoPlay) doSpeak(lesson.items[index]);
    },
    [doSpeak, savePosition, stopPlayback],
  );

  // auto-advance skips over items flagged "skip"
  const autoAdvanceNext = useCallback(() => {
    const lesson = lessonRef.current;
    if (!lesson) return;
    let idx = indexRef.current + 1;
    while (idx < lesson.items.length && skippedRef.current[idx]) idx++;
    if (idx < lesson.items.length) {
      goToIndex(idx, true);
    } else {
      stopPlayback();
    }
  }, [goToIndex, stopPlayback]);
  // doSpeak's completion handler reaches auto-advance through this ref, so it
  // always calls the latest closure regardless of declaration order.
  autoAdvanceRef.current = autoAdvanceNext;

  const playCurrent = useCallback(() => {
    const item = lessonRef.current?.items[indexRef.current];
    if (!item) return;
    playCountRef.current = 0;
    doSpeak(item);
  }, [doSpeak]);

  const replay = useCallback(() => {
    playCountRef.current = 0;
    playCurrent();
  }, [playCurrent]);

  const togglePlay = useCallback(() => {
    if (isSpeaking) {
      stopPlayback();
    } else {
      playCurrent();
    }
  }, [isSpeaking, playCurrent, stopPlayback]);

  const playItemAudio = useCallback(
    (index: number) => {
      const lesson = lessonRef.current;
      if (!lesson || index < 0 || index >= lesson.items.length) return;
      stopPlayback();
      setCurrentIndex(index);
      indexRef.current = index;
      playCountRef.current = 0;
      doSpeak(lesson.items[index]);
    },
    [doSpeak, stopPlayback],
  );

  const next = useCallback(
    (autoPlay = true) => {
      const lesson = lessonRef.current;
      if (!lesson) return;
      const nxt = indexRef.current + 1;
      if (nxt < lesson.items.length) goToIndex(nxt, autoPlay);
      else stopPlayback();
    },
    [goToIndex, stopPlayback],
  );

  const prev = useCallback(
    (autoPlay = true) => {
      const p = indexRef.current - 1;
      if (p >= 0) goToIndex(p, autoPlay);
    },
    [goToIndex],
  );

  const flipCard = useCallback(() => setCardShowingFront((f) => !f), []);

  // ---- navigation ----------------------------------------------------------
  const openUnit = useCallback(
    (lesson: Lesson) => {
      stopPlayback();
      setCurrentSid(lesson.sid);
      lessonRef.current = lesson;
      setSkipped({});
      const saved = settingsRef.current.progress.last_positions[lesson.sid] ?? 0;
      const idx = saved < lesson.items.length ? saved : 0;
      setCurrentIndex(idx);
      indexRef.current = idx;
      setCardShowingFront(true);
      setScreen('scenario_menu');
    },
    [stopPlayback],
  );

  const startCardPlay = useCallback(() => {
    playCountRef.current = 0;
    setCardShowingFront(true);
    setScreen('card_play');
  }, []);

  const startDictation = useCallback(() => {
    playCountRef.current = 0;
    setDictationInput('');
    setDictationChecked(false);
    setScreen('dictation');
  }, []);

  const startExpressionList = useCallback(() => setScreen('expression_list'), []);

  const startAddSentences = useCallback(() => {
    stopPlayback();
    setScreen('add_sentences');
  }, [stopPlayback]);

  const backToUnitList = useCallback(() => {
    stopPlayback();
    setScreen('unit_list');
  }, [stopPlayback]);

  const backToScenarioMenu = useCallback(() => {
    stopPlayback();
    setCardShowingFront(true);
    setScreen('scenario_menu');
  }, [stopPlayback]);

  // ---- data ----------------------------------------------------------------
  const nextSid = useCallback(
    () => lessons.reduce((mx, l) => Math.max(mx, l.sid), 0) + 1,
    [lessons],
  );

  const addSentences = useCallback(
    async (
      target: Lesson | null,
      part: string,
      unit: string,
      pairs: Pair[],
      voiceKey: string,
      persona: boolean,
    ): Promise<AddResult> => {
      const baseVoiceId =
        voiceKey === DEFAULT_VOICE ? undefined : voiceOptions.find((o) => o.key === voiceKey)?.identifier;

      let voiceIds: Array<string | undefined>;
      let speakTexts: string[];
      if (persona) {
        const speakers = pairs.map((p) => splitSpeaker(p.eng)[0]);
        const pool = await voiceIdentifierPool();
        voiceIds = assignPersonaVoices(speakers, baseVoiceId, pool);
        speakTexts = pairs.map((p) => splitSpeaker(p.eng)[1]);
      } else {
        voiceIds = pairs.map(() => baseVoiceId);
        speakTexts = pairs.map((p) => p.eng);
      }

      // Resolve or create the target lesson.
      let sid: number;
      let lessonPart: string;
      let lessonUnit: string;
      if (target) {
        sid = target.sid;
        lessonPart = target.part;
        lessonUnit = target.unit;
      } else {
        sid = nextSid();
        lessonPart = part.trim();
        lessonUnit = unit.trim() || `Unit ${sid}`;
      }

      const startSeq = target ? target.items.reduce((mx, it) => Math.max(mx, it.key_seq), 0) : 0;
      const newItems: PlayItem[] = pairs.map((p, i) => {
        const seq = startSeq + i + 1;
        const base = `s${sid}_${seq}.mp3`;
        const item: PlayItem = {
          ...emptyItem(),
          key_seq: seq,
          eng_text: p.eng,
          kor_text: p.kor,
          mp3_path: `/${CONTENT_ID}/${base}`,
          audio_output: base,
          pack: lessonPart,
          title: lessonUnit,
          voice: voiceIds[i],
        };
        if (persona && speakTexts[i] && speakTexts[i] !== p.eng) item.speak_text = speakTexts[i];
        return item;
      });

      const prevLessons = lessonsRef.current;
      let out: Lesson[];
      if (target) {
        out = prevLessons.map((l) => (l.sid === sid ? { ...l, items: [...l.items, ...newItems] } : l));
      } else {
        const lesson: Lesson = {
          sid,
          content_id: CONTENT_ID,
          part: lessonPart,
          unit: lessonUnit,
          course: 'Speak2Remember',
          items: newItems,
        };
        out = [...prevLessons, lesson];
      }
      const resultLesson = out.find((l) => l.sid === sid)!;
      lessonsRef.current = out;
      setLessons(out);
      void saveLessons(out);

      return { ok: pairs.length, lesson: resultLesson };
    },
    [nextSid, voiceOptions],
  );

  const deleteUnit = useCallback(
    (lesson: Lesson) => {
      setLessons((prevLessons) => {
        const out = prevLessons.filter((l) => l.sid !== lesson.sid);
        void saveLessons(out);
        return out;
      });
      // drop its saved position
      const nextPos = { ...progress.last_positions };
      delete nextPos[lesson.sid];
      persistProgress({ ...progress, last_positions: nextPos });
    },
    [persistProgress, progress],
  );

  // Web-only: localStorage is wiped when the user clears site data, so the app
  // needs a manual backup path that the Android/desktop builds don't.
  const exportData = useCallback(() => {
    downloadExport(buildExport(lessonsRef.current, settingsRef.current.progress));
  }, []);

  const importData = useCallback(
    (text: string) => {
      const parsed = parseImport(text);
      if (!parsed) return null;
      stopPlayback();
      setCurrentSid(null);
      lessonsRef.current = parsed.lessons;
      setLessons(parsed.lessons);
      void saveLessons(parsed.lessons);
      if (parsed.progress) persistProgress(parsed.progress);
      setScreen('unit_list');
      return { lessons: parsed.lessons.length };
    },
    [persistProgress, stopPlayback],
  );

  // ---- helpers -------------------------------------------------------------
  const unitProgress = useCallback(
    (lesson: Lesson) => {
      if (!lesson.items.length) return 0;
      const pos = progress.last_positions[lesson.sid];
      if (pos == null) return 0;
      return Math.min(pos + 1, lesson.items.length) / lesson.items.length;
    },
    [progress.last_positions],
  );

  // With live speech synthesis every non-empty sentence is speakable.
  const itemHasAudio = useCallback((item: PlayItem) => item.eng_text.trim().length > 0, []);
  const unitHasAnyAudio = useCallback((lesson: Lesson) => lesson.items.length > 0, []);

  const isItemSkipped = useCallback((index: number) => !!skipped[index], [skipped]);
  const toggleItemSkipped = useCallback(
    (index: number) => setSkipped((s) => ({ ...s, [index]: !s[index] })),
    [],
  );

  // ---- settings ------------------------------------------------------------
  const setSpeed = useCallback(
    (s: SpeedKey) => persistProgress({ ...progress, repeat_speed: s }),
    [persistProgress, progress],
  );
  const setRepeatCount = useCallback(
    (r: RepeatKey) => persistProgress({ ...progress, repeat_count: r }),
    [persistProgress, progress],
  );
  const toggleMeaning = useCallback(
    () => persistProgress({ ...progress, show_meaning: !progress.show_meaning }),
    [persistProgress, progress],
  );
  const toggleWord = useCallback(
    () => persistProgress({ ...progress, show_word: !progress.show_word }),
    [persistProgress, progress],
  );
  const setAutoAdvance = useCallback(
    (v: boolean) => persistProgress({ ...progress, auto_advance: v }),
    [persistProgress, progress],
  );

  const setLang = useCallback((pref: LangPref) => {
    setLangPref(pref);
    setLangState(pref === 'system' ? detectSystemLang() : pref);
    void saveUiLang(pref);
  }, []);

  // ---- dictation -----------------------------------------------------------
  const checkDictation = useCallback(() => {
    setDictationChecked(true);
    const item = currentItem;
    if (!item) return false;
    return normalize(dictationInput) === normalize(item.eng_text);
  }, [currentItem, dictationInput]);

  const value: StoreValue = {
    ready,
    screen,
    lessons,
    currentLesson,
    currentIndex,
    currentItem,
    cardShowingFront,
    isSpeaking,
    progress,
    voiceOptions,
    speechSupported,
    dictationInput,
    dictationChecked,
    setDictationInput,
    lang,
    langPref,
    t,
    setLang,
    openUnit,
    startCardPlay,
    startDictation,
    startExpressionList,
    startAddSentences,
    backToUnitList,
    backToScenarioMenu,
    nextSid,
    addSentences,
    deleteUnit,
    exportData,
    importData,
    unitProgress,
    itemHasAudio,
    unitHasAnyAudio,
    isItemSkipped,
    toggleItemSkipped,
    playCurrent,
    replay,
    togglePlay,
    stopPlayback,
    playItemAudio,
    goToIndex,
    next,
    prev,
    flipCard,
    setSpeed,
    setRepeatCount,
    toggleMeaning,
    toggleWord,
    setAutoAdvance,
    checkDictation,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
